// Demo dataset for the mock backend.
//
// Dates are generated relative to "today" so the Calendar view and due-date
// chips always show live content. Property values follow the server encodings
// verified against upstream sources:
//   select / multiSelect -> option id (or array of them)
//   number               -> plain digits string
//   date                 -> millisecond epoch string
//   person               -> user id
//   checkbox             -> "true" (absent means unchecked)

import {Block} from '../types/block'
import {Board, BoardTypePrivate, IPropertyTemplate, MemberRole, PropertyTypeEnum} from '../types/board'
import {BoardView, createBoardView, IViewType, viewTitleOf} from '../types/boardView'
import {Card, createCard} from '../types/card'
import {CategoryBoards} from '../types/category'
import {ITeam, IUser} from '../types/user'
import {Constants} from '../common/constants'

const DAY = 86400000
const HOUR = 3600000

function at(offsetDays: number, hour = 12): number {
    const d = new Date()
    d.setHours(hour, 0, 0, 0)
    return d.getTime() + offsetDays * DAY
}

export const TEAM_ID = 'team-acme-01'
export const ME_ID = 'user-alice'

const teams: ITeam[] = [
    {id: TEAM_ID, title: '顶峰科技', signupToken: '', settings: {read_only_privacy: 'private'}},
]

function makeUser(id: string, username: string, firstname: string, lastname: string): IUser {
    return {
        id,
        username,
        email: `${username}@dingzhi.cn`,
        nickname: firstname,
        firstname,
        lastname,
        props: {},
        create_at: at(-400),
        update_at: at(-400),
        is_bot: false,
        is_guest: false,
        roles: 'system_admin',
    }
}

const users: IUser[] = [
    makeUser(ME_ID, '张伟', '张伟', ''),
    makeUser('user-bob', '李娜', '李娜', ''),
    makeUser('user-carol', '王芳', '王芳', ''),
    makeUser('user-dan', '刘洋', '刘洋', ''),
]

// ------------------------------------------------------------- shape ------

type OptSpec = [id: string, value: string, color: string]
type PropSpec = {id: string, name: string, type: PropertyTypeEnum, options?: OptSpec[]}
type ContentSpec = {type: Block['type'], title: string, checked?: boolean}
type CommentSpec = {author: string, text: string, offset: number}

type CardSpec = {
    id: string
    title: string
    icon?: string
    values: Record<string, string>
    createdBy?: string
    createdOffset: number
    content?: ContentSpec[]
    comments?: CommentSpec[]
}

type BoardSpec = {
    id: string
    title: string
    icon: string
    description: string
    showDescription?: boolean
    views: IViewType[]
    properties: PropSpec[]
    cards: CardSpec[]
}

const S = {
    todo: 'propColorGray',
    doing: 'propColorBlue',
    review: 'propColorYellow',
    done: 'propColorGreen',
}

const taskStatus: PropSpec = {
    id: 'status',
    name: '状态',
    type: 'select',
    options: [
        ['st-todo', '未开始', S.todo],
        ['st-doing', '进行中', S.doing],
        ['st-review', '待评审', S.review],
        ['st-done', '已完成', S.done],
    ],
}

const boards: BoardSpec[] = [
    {
        id: 'board-pt',
        title: '项目任务',
        icon: '📋',
        description: '团队待办清单。可把卡片在列之间拖拽，或用下方页签切换表格、画廊、日历视图。',
        showDescription: true,
        views: ['board', 'table', 'gallery', 'calendar'],
        properties: [
            taskStatus,
            {
                id: 'priority',
                name: '优先级',
                type: 'select',
                options: [
                    ['pr-low', '低', 'propColorGray'],
                    ['pr-med', '中', 'propColorYellow'],
                    ['pr-high', '高', 'propColorRed'],
                ],
            },
            {id: 'size', name: '规模', type: 'number'},
            {id: 'owner', name: '负责人', type: 'person'},
            {id: 'due', name: '截止日期', type: 'date'},
            {
                id: 'blockers',
                name: '阻塞项',
                type: 'multiSelect',
                options: [
                    ['bl-review', '等待评审', 'propColorPurple'],
                    ['bl-spec', '需求不明', 'propColorOrange'],
                    ['bl-env', '环境问题', 'propColorBrown'],
                ],
            },
            {id: 'verified', name: '已验收', type: 'checkbox'},
        ],
        cards: [
            {
                id: 'card-pt-01',
                title: '搭建持续集成流水线',
                icon: '⚙️',
                values: {status: 'st-doing', priority: 'pr-high', size: '5', owner: 'user-bob', due: String(at(2))},
                createdOffset: -9,
                content: [
                    {type: 'h2', title: '完成定义'},
                    {type: 'text', title: '每次推送到主分支都会执行构建、类型检查与单元测试。'},
                    {type: 'checkbox', title: '选定执行机镜像', checked: true},
                    {type: 'checkbox', title: '缓存依赖目录', checked: true},
                    {type: 'checkbox', title: '把前端产物发布为构建产物', checked: false},
                    {type: 'divider', title: ''},
                    {type: 'text', title: '部署机器人还缺镜像仓库的访问权限。'},
                ],
                comments: [
                    {author: 'user-carol', text: '执行机镜像已经推到共享仓库了，可以直接拉取。', offset: -2},
                    {author: 'user-bob', text: '加了缓存后流水线快了大约 90 秒。', offset: -1},
                ],
            },
            {id: 'card-pt-02', title: '设计令牌盘点', values: {status: 'st-review', priority: 'pr-med', size: '3', owner: 'user-alice', due: String(at(0)), blockers: 'bl-review'}, createdOffset: -12},
            {id: 'card-pt-03', title: '认证迁移到会话服务', values: {status: 'st-todo', priority: 'pr-high', size: '8', owner: 'user-carol', due: String(at(9))}, createdOffset: -4},
            {id: 'card-pt-04', title: '空状态插画', values: {status: 'st-todo', priority: 'pr-low', size: '2', owner: 'user-dan'}, createdOffset: -3},
            {id: 'card-pt-05', title: '看板触摸拖拽手柄', values: {status: 'st-doing', priority: 'pr-med', size: '3', owner: 'user-bob', due: String(at(4))}, createdOffset: -7},
            {
                id: 'card-pt-06',
                title: '表格列宽持久化',
                values: {status: 'st-done', priority: 'pr-med', size: '2', owner: 'user-alice', due: String(at(-3)), verified: 'true'},
                createdOffset: -20,
                content: [{type: 'text', title: '列宽存在视图块上，所以它跟随视图而不是跟随用户。'}],
            },
            {id: 'card-pt-07', title: '画廊封面图', values: {status: 'st-done', priority: 'pr-low', size: '1', owner: 'user-dan', due: String(at(-6))}, createdOffset: -25},
            {
                id: 'card-pt-08',
                title: '卡片详情键盘导航',
                icon: '⌨️',
                values: {status: 'st-review', priority: 'pr-high', size: '5', owner: 'user-carol', due: String(at(1))},
                createdOffset: -11,
                content: [
                    {type: 'h1', title: '快捷键'},
                    {type: 'text', title: '按退出键关闭卡片，上下方向键在卡片之间切换。'},
                    {type: 'checkbox', title: '弹层内焦点收束', checked: false},
                ],
                comments: [{author: 'user-alice', text: '焦点切换顺序请和属性列表的顺序保持一致。', offset: -1}],
            },
            {id: 'card-pt-09', title: '批量归档旧卡片', values: {status: 'st-todo', priority: 'pr-low', size: '3', owner: 'user-bob', due: String(at(15)), blockers: 'bl-spec'}, createdOffset: -2},
            {id: 'card-pt-10', title: '导出看板为表格文件', values: {status: 'st-done', priority: 'pr-med', size: '2', owner: 'user-alice', due: String(at(-1))}, createdOffset: -18},
            {id: 'card-pt-11', title: '侧边栏分组排序', values: {status: 'st-doing', priority: 'pr-med', size: '3', owner: 'user-dan', due: String(at(6))}, createdOffset: -6},
            {id: 'card-pt-12', title: '主题切换（默认 / 浅色 / 深色）', values: {status: 'st-todo', priority: 'pr-high', size: '5', owner: 'user-carol', due: String(at(11))}, createdOffset: -1},
        ],
    },
    {
        id: 'board-rm',
        title: '产品路线图',
        icon: '🚀',
        description: '近期 / 中期 / 远期规划，带目标日期。日历视图按「目标日期」排布。',
        views: ['board', 'table', 'gallery', 'calendar'],
        properties: [
            {
                id: 'status',
                name: '状态',
                type: 'select',
                options: [
                    ['rm-now', '近期', S.doing],
                    ['rm-next', '中期', S.review],
                    ['rm-later', '远期', S.todo],
                    ['rm-published', '已发布', S.done],
                ],
            },
            {id: 'owner', name: '负责人', type: 'person'},
            {id: 'target', name: '目标日期', type: 'date'},
        ],
        cards: [
            {id: 'card-rm-01', title: '实时协同', icon: '🛰️', values: {status: 'rm-now', owner: 'user-alice', target: String(at(21))}, createdOffset: -30},
            {id: 'card-rm-02', title: '看板模板库', values: {status: 'rm-now', owner: 'user-carol', target: String(at(14))}, createdOffset: -28},
            {id: 'card-rm-03', title: '移动端布局走查', values: {status: 'rm-next', owner: 'user-dan', target: String(at(45))}, createdOffset: -22},
            {id: 'card-rm-04', title: '日历日视图', values: {status: 'rm-next', owner: 'user-bob', target: String(at(52))}, createdOffset: -20},
            {id: 'card-rm-05', title: '公式属性', values: {status: 'rm-later', owner: 'user-alice', target: String(at(96))}, createdOffset: -15},
            {id: 'card-rm-06', title: '公开分享链接', values: {status: 'rm-published', owner: 'user-carol', target: String(at(-18))}, createdOffset: -60},
            {id: 'card-rm-07', title: '从其他看板工具导入', values: {status: 'rm-published', owner: 'user-dan', target: String(at(-40))}, createdOffset: -80},
        ],
    },
    {
        id: 'board-bg',
        title: '缺陷跟踪',
        icon: '🐞',
        description: '已提交的缺陷，按状态分组。',
        views: ['board', 'table', 'gallery', 'calendar'],
        properties: [
            {
                id: 'status',
                name: '状态',
                type: 'select',
                options: [
                    ['st-todo', '待处理', S.todo],
                    ['st-doing', '进行中', S.doing],
                    ['st-review', '待评审', S.review],
                    ['st-done', '已解决', S.done],
                ],
            },
            {
                id: 'severity',
                name: '严重程度',
                type: 'select',
                options: [
                    ['sv-low', '轻微', 'propColorGray'],
                    ['sv-med', '严重', 'propColorYellow'],
                    ['sv-high', '致命', 'propColorRed'],
                ],
            },
            {id: 'owner', name: '负责人', type: 'person'},
            {id: 'due', name: '截止日期', type: 'date'},
        ],
        cards: [
            {id: 'card-bg-01', title: '列头在 260 像素处换行', values: {status: 'st-doing', severity: 'sv-med', owner: 'user-bob', due: String(at(1))}, createdOffset: -3},
            {id: 'card-bg-02', title: '日期标签用错语言环境', values: {status: 'st-todo', severity: 'sv-low', owner: 'user-dan'}, createdOffset: -5},
            {
                id: 'card-bg-03',
                title: '卡片弹层打开时滚动跳动',
                icon: '🐞',
                values: {status: 'st-review', severity: 'sv-high', owner: 'user-alice', due: String(at(0))},
                createdOffset: -8,
                content: [{type: 'text', title: '仅在属性列表高度超过视口时才能复现。'}],
            },
            {id: 'card-bg-04', title: '刷新后分组的收起状态丢失', values: {status: 'st-done', severity: 'sv-med', owner: 'user-carol', due: String(at(-2))}, createdOffset: -14},
            {id: 'card-bg-05', title: '画廊标题截断会切掉表情符号', values: {status: 'st-todo', severity: 'sv-low', owner: 'user-bob', due: String(at(7))}, createdOffset: -2},
        ],
    },
    {
        id: 'board-mt',
        title: '会议纪要',
        icon: '🗓️',
        description: '每次会议一张卡片，打开即可查看纪要与评论。',
        views: ['table', 'board', 'calendar'],
        properties: [
            {
                id: 'topic',
                name: '主题',
                type: 'select',
                options: [
                    ['tp-sync', '同步会', 'propColorBlue'],
                    ['tp-design', '设计', 'propColorPurple'],
                    ['tp-retro', '回顾', 'propColorGreen'],
                ],
            },
            {id: 'when', name: '日期', type: 'date'},
        ],
        cards: [
            {
                id: 'card-mt-01',
                title: '周例会 —— 版本封板',
                icon: '🗓️',
                values: {topic: 'tp-sync', when: String(at(-1))},
                createdOffset: -1,
                content: [
                    {type: 'h2', title: '议程'},
                    {type: 'text', title: '先过一遍发布阻塞项，再看日历视图的回归问题。'},
                    {type: 'list-item', title: '确认代码冻结日期'},
                    {type: 'list-item', title: '指定迁移负责人'},
                ],
                comments: [{author: 'user-dan', text: '如果没人接，迁移这件事我可以来。', offset: 0}],
            },
            {
                id: 'card-mt-02',
                title: '设计评审：卡片详情',
                values: {topic: 'tp-design', when: String(at(-4))},
                createdOffset: -4,
                content: [{type: 'text', title: '桌面端加大内边距，属性固定在右列。'}],
            },
            {id: 'card-mt-03', title: '回顾会 —— 该停掉什么', values: {topic: 'tp-retro', when: String(at(3))}, createdOffset: 0},
        ],
    },
]

const templates: Array<{id: string, title: string, icon: string, properties: PropSpec[]}> = [
    {id: 'tpl-blank', title: '空白看板', icon: '⬜', properties: []},
    {id: 'tpl-kanban', title: '项目任务', icon: '📋', properties: [taskStatus]},
    {
        id: 'tpl-checklist',
        title: '清单',
        icon: '✅',
        properties: [{id: 'done', name: '已完成', type: 'checkbox'}],
    },
]

// ------------------------------------------------------------ builders ----

function toPropertyTemplates(specs: PropSpec[]): IPropertyTemplate[] {
    return specs.map((p) => ({
        id: p.id,
        name: p.name,
        type: p.type,
        options: (p.options || []).map(([id, value, color]) => ({id, value, color})),
    }))
}

function makeBoard(id: string, spec: {title: string, icon: string, description: string, showDescription?: boolean}, properties: IPropertyTemplate[], isTemplate: boolean): Board {
    const now = Date.now()
    return {
        id,
        teamId: TEAM_ID,
        channelId: Constants.noChannelID,
        createdBy: ME_ID,
        modifiedBy: ME_ID,
        type: BoardTypePrivate,
        minimumRole: MemberRole.None,
        title: spec.title,
        description: spec.description,
        icon: spec.icon,
        showDescription: Boolean(spec.showDescription),
        isTemplate,
        templateVersion: isTemplate ? 3 : 0,
        properties: {},
        cardProperties: properties,
        createAt: at(-90),
        updateAt: now,
        deleteAt: 0,
    }
}

function makeViews(b: Board, cardIds: string[], viewTypes: IViewType[]): BoardView[] {
    const selectProp = b.cardProperties.find((p) => p.type === 'select')
    const dateProp = b.cardProperties.find((p) => p.type === 'date')

    return viewTypes.map((viewType, index) => {
        const view = createBoardView({
            id: `view-${b.id}-${index}`,
            boardId: b.id,
            parentId: b.id,
            type: 'view',
            title: viewTitleOf(viewType),
            createdBy: ME_ID,
            modifiedBy: ME_ID,
            createAt: b.createAt + index,
            updateAt: b.updateAt,
        })
        view.fields.viewType = viewType
        view.fields.cardOrder = cardIds.slice()
        view.fields.visiblePropertyIds = b.cardProperties.map((p) => p.id)
        view.fields.columnWidths = {__title: Constants.defaultTitleColumnWidth}
        view.fields.groupById = viewType === 'table' || viewType === 'calendar' ? undefined : selectProp?.id
        view.fields.dateDisplayPropertyId = viewType === 'calendar' ? dateProp?.id : undefined
        view.fields.collapsedOptionIds = []
        return view
    })
}

function makeCard(b: Board, spec: CardSpec): {card: Card, blocks: Block[]} {
    const card = createCard({
        id: spec.id,
        boardId: b.id,
        parentId: b.id,
        type: 'card',
        title: spec.title,
        createdBy: spec.createdBy || 'user-bob',
        modifiedBy: ME_ID,
        createAt: at(spec.createdOffset, 9),
        updateAt: at(spec.createdOffset, 9) + 3 * HOUR,
    })
    card.fields.icon = spec.icon || ''
    card.fields.properties = {...spec.values}

    const blocks: Block[] = []
    const contentOrder: Array<string | string[]> = []
    const contentSpecs = spec.content || []

    contentSpecs.forEach((c, i) => {
        const id = `${spec.id}-content-${i}`
        contentOrder.push(id)
        blocks.push({
            id,
            boardId: b.id,
            parentId: spec.id,
            createdBy: card.createdBy,
            modifiedBy: card.createdBy,
            schema: 1,
            type: c.type,
            title: c.title,
            fields: c.checked === undefined ? {} : {value: c.checked},
            createAt: at(spec.createdOffset, 10),
            updateAt: at(spec.createdOffset, 10),
            deleteAt: 0,
        })
    })
    card.fields.contentOrder = contentOrder

    const commentSpecs = spec.comments || []
    commentSpecs.forEach((c, i) => {
        blocks.push({
            id: `${spec.id}-comment-${i}`,
            boardId: b.id,
            parentId: spec.id,
            createdBy: c.author,
            modifiedBy: c.author,
            schema: 1,
            type: 'comment',
            title: c.text,
            fields: {},
            createAt: at(c.offset, 15),
            updateAt: at(c.offset, 15),
            deleteAt: 0,
        })
    })

    return {card, blocks}
}

export type SeedData = {
    teams: ITeam[]
    users: IUser[]
    boards: Board[]
    blocks: Block[]
    categories: CategoryBoards[]
    credentials: Array<{userId: string, username: string, password: string}>
}

export function buildSeed(): SeedData {
    const outBoards: Board[] = []
    const outBlocks: Block[] = []

    boards.forEach((spec) => {
        const b = makeBoard(spec.id, spec, toPropertyTemplates(spec.properties), false)
        outBoards.push(b)

        const cards = spec.cards.map((c) => {
            const {card, blocks} = makeCard(b, c)
            outBlocks.push(card)
            outBlocks.push(...blocks)
            return card
        })
        outBlocks.push(...makeViews(b, cards.map((c) => c.id), spec.views))
    })

    templates.forEach((t) => {
        const b = makeBoard(t.id, {title: t.title, icon: t.icon, description: '初始模板'}, toPropertyTemplates(t.properties), true)
        outBoards.push(b)
        outBlocks.push(...makeViews(b, [], ['board', 'table', 'gallery', 'calendar']))
    })

    const categories: CategoryBoards[] = [
        {
            id: 'cat-plans',
            name: '计划',
            userID: ME_ID,
            teamID: TEAM_ID,
            createAt: at(-90),
            updateAt: Date.now(),
            deleteAt: 0,
            collapsed: false,
            sortOrder: 1,
            type: 'custom',
            isNew: false,
            boardMetadata: [
                {boardID: 'board-pt', hidden: false},
                {boardID: 'board-rm', hidden: false},
            ],
        },
        {
            id: 'cat-ops',
            name: '团队事务',
            userID: ME_ID,
            teamID: TEAM_ID,
            createAt: at(-90),
            updateAt: Date.now(),
            deleteAt: 0,
            collapsed: false,
            sortOrder: 2,
            type: 'custom',
            isNew: false,
            boardMetadata: [
                {boardID: 'board-bg', hidden: false},
                {boardID: 'board-mt', hidden: false},
            ],
        },
    ]

    return {
        teams,
        users,
        boards: outBoards,
        blocks: outBlocks,
        categories,
        credentials: [
            {userId: ME_ID, username: '张伟', password: '演示'},
            {userId: 'user-bob', username: '李娜', password: '演示'},
            {userId: 'user-carol', username: '王芳', password: '演示'},
            {userId: 'user-dan', username: '刘洋', password: '演示'},
        ],
    }
}
